import React, { useEffect, useState } from 'react';
import { useAuth } from '../../utils/AuthContext';
import {
  type EmployeeBenefitsSettingsDoc,
  saveEmployeeBenefitsSettings,
  subscribeEmployeeBenefitsSettings,
} from '../../utils/peopleHubFirestore';
import { POLICY_COMPANY_HOLIDAY_YEARS } from '../../data/companyHolidaysPolicy';
import { format } from 'date-fns';
import '../../pages/PeopleHubPage.css';

/**
 * Admin-only: People hub defaults (team calendar width) + benefits copy + company holidays.
 * Shown under Settings → Admin.
 */
const PeopleHubBenefitsAdminSettings: React.FC = () => {
  const { user } = useAuth();
  const [draft, setDraft] = useState<EmployeeBenefitsSettingsDoc | null>(null);

  useEffect(() => {
    if (user?.role !== 'admin') return;
    const unsub = subscribeEmployeeBenefitsSettings(
      d => setDraft(d),
      e => console.error('[PeopleHubBenefitsAdminSettings]', e),
    );
    return unsub;
  }, [user?.role]);

  const save = async () => {
    if (!draft || user?.role !== 'admin') return;
    try {
      await saveEmployeeBenefitsSettings(draft);
    } catch (e) {
      console.error(e);
      alert(e instanceof Error ? e.message : 'Save failed');
    }
  };

  if (user?.role !== 'admin') return null;
  if (!draft) {
    return (
      <div className="setting-item">
        <p className="setting-description">Loading People hub settings…</p>
      </div>
    );
  }

  return (
    <div className="setting-item people-hub-settings-admin-wrap">
      <h3>People hub — calendar &amp; benefits</h3>
      <p className="setting-description">
        Defaults for the team calendar, SEP and health copy on the Benefits page, and company holidays shown on the
        calendar.
      </p>

      <div className="people-hub-settings-block">
        <h4 className="people-hub-settings-subhead">Team calendar default</h4>
        <p className="setting-description people-hub-settings-tight">
          How many months wide the calendar opens for everyone (they can still change it on the calendar page).
        </p>
        <div className="ops-hub-views" role="group" aria-label="Default months visible">
          {([2, 3, 6] as const).map(n => (
            <button
              key={n}
              type="button"
              className={`ops-view-btn ${draft.peopleHubDefaultCalendarMonthsVisible === n ? 'active' : ''}`}
              aria-pressed={draft.peopleHubDefaultCalendarMonthsVisible === n}
              onClick={() => setDraft({ ...draft, peopleHubDefaultCalendarMonthsVisible: n })}
            >
              {n} mo
            </button>
          ))}
        </div>
        <button
          type="button"
          className="pm-btn pm-btn-ghost pm-btn-sm people-hub-settings-clear"
          onClick={() => setDraft({ ...draft, peopleHubDefaultCalendarMonthsVisible: null })}
        >
          Clear default (use 3 months until user changes)
        </button>
      </div>

      <div className="setting-divider" />

      <div className="people-hub-settings-block">
        <h4 className="people-hub-settings-subhead">Benefits copy</h4>
        <div className="ops-modal-row">
          <label className="ops-modal-label" htmlFor="settings-people-sep">
            SEP %
          </label>
          <input
            id="settings-people-sep"
            className="ops-modal-input"
            type="number"
            min={0}
            max={100}
            step={0.5}
            value={draft.sepIraContributionPercent ?? ''}
            onChange={e =>
              setDraft({
                ...draft,
                sepIraContributionPercent: e.target.value === '' ? null : Number(e.target.value),
              })
            }
          />
        </div>
        <div className="ops-modal-section">
          <label className="ops-modal-label" htmlFor="settings-people-health">
            Health reimbursement summary
          </label>
          <textarea
            id="settings-people-health"
            className="ops-modal-textarea"
            rows={4}
            value={draft.healthInsuranceReimbursementSummary}
            onChange={e => setDraft({ ...draft, healthInsuranceReimbursementSummary: e.target.value })}
          />
        </div>
      </div>

      <div className="setting-divider" />

      <div className="people-hub-settings-block people-hub-holidays">
        <h4 className="people-hub-settings-subhead">Company holidays</h4>
        <p className="people-hub-muted people-hub-holidays-hint">
          The list always includes the Section 5 template for <strong>{POLICY_COMPANY_HOLIDAY_YEARS}</strong> (plus any
          extra dates you add). Edits here override the template for that calendar day when you save.
        </p>
        {draft.holidays.map((h, idx) => (
          <div key={h.id} className="people-hub-holiday-row">
            <input
              className="ops-modal-input"
              type="text"
              placeholder="Name"
              value={h.name}
              onChange={e => {
                const next = [...draft.holidays];
                next[idx] = { ...h, name: e.target.value };
                setDraft({ ...draft, holidays: next });
              }}
            />
            <input
              className="ops-modal-date"
              type="date"
              value={h.date}
              onChange={e => {
                const next = [...draft.holidays];
                next[idx] = { ...h, date: e.target.value };
                setDraft({ ...draft, holidays: next });
              }}
            />
            <button
              type="button"
              className="pm-btn pm-btn-sm"
              onClick={() => {
                const next = draft.holidays.filter((_, i) => i !== idx);
                setDraft({ ...draft, holidays: next });
              }}
            >
              Remove
            </button>
          </div>
        ))}
        <div className="people-hub-inline-actions">
          <button
            type="button"
            className="pm-btn"
            onClick={() =>
              setDraft({
                ...draft,
                holidays: [...draft.holidays, { id: `h_${Date.now()}`, name: '', date: format(new Date(), 'yyyy-MM-dd') }],
              })
            }
          >
            Add holiday
          </button>
          <button type="button" className="pm-btn pm-btn-primary" onClick={() => void save()}>
            Save People hub settings
          </button>
        </div>
      </div>
    </div>
  );
};

export default PeopleHubBenefitsAdminSettings;
